/**
 * TOC builder. Walks the rendered HTML once, parses h1..h6 with their
 * anchor IDs (added by markdown-it-anchor in the render pipeline), and
 * folds them into a nested tree the Toc.svelte sidebar renders directly.
 *
 * Stack-based nesting: each new heading pops the stack down to the first
 * entry strictly shallower than itself, then attaches as a child of the
 * stack top (or the root list when the stack is empty). Skipped levels
 * (h1 directly to h3) collapse into the nearest available ancestor —
 * preferable to inserting phantom h2 nodes that wouldn't exist in the
 * source. Matches the spec's "graceful handling" requirement.
 *
 * Headings without an id are skipped — the TOC's value is jump-to-anchor;
 * an entry with no anchor is dead weight.
 */

export interface TocNode {
  id: string
  text: string
  level: number
  children: TocNode[]
}

// jsdom (test env) and the browser both expose DOMParser. The render pipeline
// runs in the same window context as the consumer, so building a temporary
// document is the cheapest reliable way to walk headings without re-parsing
// markdown.
function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html')
}

export function buildToc(html: string): TocNode[] {
  if (!html.trim()) return []

  const doc = parseHtml(html)
  const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6')
  if (headings.length === 0) return []

  const roots: TocNode[] = []
  // Stack of nodes whose `children` we may still extend. Each entry is the
  // most recent heading at its level. A new heading at the same or shallower
  // level pops entries until the stack top is strictly shallower.
  const stack: TocNode[] = []

  headings.forEach((el) => {
    const id = el.getAttribute('id')
    if (!id) return

    const level = Number(el.tagName.slice(1))
    const text = (el.textContent ?? '').trim()
    const node: TocNode = { id, text, level, children: [] }

    while (stack.length > 0 && stack[stack.length - 1]!.level >= level) {
      stack.pop()
    }

    if (stack.length === 0) {
      roots.push(node)
    } else {
      stack[stack.length - 1]!.children.push(node)
    }

    stack.push(node)
  })

  return roots
}
